import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Users, Star, Lock, Clock, ChevronRight, Filter, Loader, CheckCircle, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMAT_LABELS = {
  open_play: "Open Play",
  doubles: "Doubles Match",
  practice: "Practice / Drill",
  mixer: "Skill-Matched Mixer",
  ladder_lite: "Ladder-Lite",
  "1v1": "1-on-1 Match",
  doubles_rotation: "Doubles Rotation",
  match_night: "Match Night",
  drill_social: "Drill Social",
};

const FORMAT_COLORS = {
  open_play: "bg-blue-500/15 text-blue-400 border-blue-400/30",
  doubles: "bg-purple-500/15 text-purple-400 border-purple-400/30",
  practice: "bg-brand-green/10 text-brand-green border-brand-green/30",
  mixer: "bg-yellow-500/15 text-yellow-400 border-yellow-400/30",
  ladder_lite: "bg-orange-500/15 text-orange-400 border-orange-400/30",
  doubles_rotation: "bg-purple-500/15 text-purple-400 border-purple-400/30",
  match_night: "bg-red-500/15 text-red-400 border-red-400/30",
  drill_social: "bg-teal-500/15 text-teal-400 border-teal-400/30",
};

const SPORTS = ["All Sports", "tennis", "pickleball", "golf", "padel", "squash", "badminton"];

function SportIcon({ sport }) {
  const icons = { tennis: "🎾", pickleball: "🏓", golf: "⛳", padel: "🎾", squash: "🏸", badminton: "🏸" };
  return <span aria-hidden="true">{icons[sport] || "🏅"}</span>;
}

function SessionCard({ session, onJoin, joining, joined, waitlisted }) {
  const isFull = session.is_full;
  const price = session.price > 0 ? `$${session.price}` : "Free";
  const formatKey = session.format || session.session_type || "open_play";
  const formatColor = FORMAT_COLORS[formatKey] || FORMAT_COLORS.open_play;
  const formatLabel = FORMAT_LABELS[formatKey] || formatKey;

  return (
    <article
      className="bg-[#161B28] border border-white/8 rounded-2xl p-5 hover:border-brand-green/25 transition-all group dark-section"
      data-testid="group-session-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full border ${formatColor}`}>{formatLabel}</span>
            {session.price > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full border bg-brand-orange/10 text-brand-orange border-brand-orange/30">{price}</span>
            )}
            {session.price === 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full border bg-green-500/10 text-green-400 border-green-400/30">Free</span>
            )}
          </div>
          <h2 className="text-base font-bold text-white group-hover:text-brand-green transition-colors truncate">
            <Link to={`/group-sessions/${session.id}`} data-testid="session-title-link">{session.title}</Link>
          </h2>
        </div>
        <div className="flex items-center gap-1 text-brand-green flex-shrink-0" aria-label={`Sport: ${session.sport}`}>
          <SportIcon sport={session.sport} />
          <span className="text-xs capitalize text-gray-400">{session.sport}</span>
        </div>
      </div>

      <div className="space-y-1.5 mb-3">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Calendar size={12} aria-hidden="true" />
          <span>{session.date_time}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <MapPin size={12} aria-hidden="true" />
          <span className="truncate">{session.location}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Users size={12} aria-hidden="true" />
          <span>
            {session.participants_count}/{session.capacity} spots
            {session.waitlist_count > 0 && <span className="text-yellow-400 ml-1">· {session.waitlist_count} on waitlist</span>}
          </span>
        </div>
        {(session.skill_min || session.skill_max) && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Star size={12} aria-hidden="true" />
            <span className="capitalize">{session.skill_min} – {session.skill_max}</span>
          </div>
        )}
      </div>

      {session.description && (
        <p className="text-xs text-gray-500 mb-3">{session.description}</p>
      )}

      {/* Capacity bar */}
      <div className="mb-4" role="progressbar" aria-valuenow={session.participants_count} aria-valuemax={session.capacity} aria-label="Session capacity">
        <div className="h-1.5 bg-white/8 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${isFull ? "bg-red-400" : "bg-brand-green"}`}
            style={{ width: `${Math.min((session.participants_count / Math.max(session.capacity, 1)) * 100, 100)}%` }}
          />
        </div>
        <p className="text-xs text-gray-600 mt-1">{session.capacity - session.participants_count} spots remaining</p>
      </div>

      {/* Action */}
      {joined ? (
        <div className="flex items-center gap-2 text-sm text-brand-green font-medium" data-testid="joined-indicator">
          <CheckCircle size={14} /> You're in!
        </div>
      ) : waitlisted ? (
        <div className="flex items-center gap-2 text-sm text-yellow-400 font-medium" data-testid="waitlisted-indicator">
          <Clock size={14} /> On waitlist
        </div>
      ) : (
        <button
          onClick={() => onJoin(session.id, session.price)}
          disabled={joining === session.id}
          className={`w-full py-2.5 text-sm font-bold rounded-xl transition-all ${
            isFull
              ? "bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20"
              : "bg-brand-green text-brand-navy hover:bg-brand-green/90"
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          data-testid="join-session-btn"
          aria-label={isFull ? `Join waitlist for ${session.title}` : `Join ${session.title}`}
        >
          {joining === session.id ? (
            <span className="flex items-center justify-center gap-2"><Loader size={14} className="animate-spin" /> Joining…</span>
          ) : isFull ? "Join Waitlist" : "Join Session"}
        </button>
      )}
      <Link to={`/group-sessions/${session.id}`} className="mt-2 flex items-center justify-center gap-1 text-xs text-gray-400 hover:text-brand-green transition-colors" data-testid="view-details-link">
        View details <ChevronRight size={12} />
      </Link>
    </article>
  );
}

export default function GroupSessionsPage() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sport, setSport] = useState("All Sports");
  const [joining, setJoining] = useState(null);
  const [joinedIds, setJoinedIds] = useState([]);
  const [waitlistedIds, setWaitlistedIds] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sport !== "All Sports") params.set("sport", sport);
      const r = await fetch(`${API}/group-sessions?${params}`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setSessions(data.sessions || []);
      }
    } catch {
      setError("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  }, [sport]);

  useEffect(() => { load(); }, [load]);

  const handleJoin = async (sessionId, price) => {
    if (!user) {
      window.location.href = "/login?next=/group-sessions";
      return;
    }
    setJoining(sessionId);
    try {
      const r = await fetch(`${API}/group-sessions/${sessionId}/join`, {
        method: "POST",
        credentials: "include",
      });
      const data = await r.json();
      if (data.requires_payment) {
        // Redirect to Stripe checkout
        const origin = window.location.origin;
        const co = await fetch(`${API}/payments/checkout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            lookup_key: "group_session_5",
            quantity: 1,
            origin_url: origin,
            user_id: user?.id,
            metadata: { session_id: sessionId },
          }),
        });
        const coData = await co.json();
        if (coData.checkout_url) window.location.href = coData.checkout_url;
        return;
      }
      if (data.status === "joined") setJoinedIds((p) => [...p, sessionId]);
      else if (data.status === "waitlisted") setWaitlistedIds((p) => [...p, sessionId]);
      load();
    } catch {
      setError("Failed to join session. Please try again.");
    } finally {
      setJoining(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1629] pt-24 pb-16 dark-section">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-heading font-black text-white mb-3">Open Sessions</h1>
          <p className="text-gray-400 max-w-xl">
            Join skill-matched group sessions, doubles matches, and open play near you. No partner needed — just show up.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-8" role="group" aria-label="Filter by sport">
          {SPORTS.map((s) => (
            <button
              key={s}
              onClick={() => setSport(s)}
              className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                sport === s
                  ? "bg-brand-green text-brand-navy border-brand-green"
                  : "bg-white/5 text-gray-300 border-white/20 hover:bg-white/10 hover:text-white"
              }`}
              aria-pressed={sport === s}
              data-testid={`filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {s}
            </button>
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-400 bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-3 mb-6" role="alert">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader size={28} className="text-brand-green animate-spin" aria-label="Loading sessions" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
              <Calendar size={28} className="text-gray-500" aria-hidden="true" />
            </div>
            <p className="text-gray-400 mb-2 text-lg font-medium">No open sessions yet</p>
            <p className="text-gray-600 text-sm mb-6">Check back soon — organizers are scheduling sessions in your area.</p>
            {user && (
              <Link
                to="/find-partners"
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green text-brand-navy font-bold rounded-xl hover:bg-brand-green/90 transition-colors"
              >
                Find a 1-on-1 Partner <ChevronRight size={16} />
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sessions.map((s) => (
              <SessionCard
                key={s.id}
                session={s}
                onJoin={handleJoin}
                joining={joining}
                joined={joinedIds.includes(s.id)}
                waitlisted={waitlistedIds.includes(s.id)}
              />
            ))}
          </div>
        )}

        {/* CTA for organizers */}
        <div className="mt-12 bg-gradient-to-r from-brand-navy/40 to-[#1E2436] border border-white/8 rounded-2xl p-6 text-center dark-section">
          <p className="text-white font-bold mb-1">Run a local session?</p>
          <p className="text-gray-400 text-sm mb-4">Apply to become an organizer and create your own skill-matched group sessions.</p>
          <Link
            to="/organizer/apply"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-orange text-white font-bold rounded-xl hover:bg-brand-orange/90 transition-colors text-sm"
            data-testid="become-organizer-btn"
          >
            Become an Organizer <ChevronRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
